import { ICommentDto, ICommentReaction } from "@kuika/kuika-cl-model";
import React from "react";
import { createPortal } from "react-dom";
import { displayNameOf } from "../../../management/board/people-directory";
import { EMOJI_SET, formatReactors } from "./comments-panel-logic";
import { IPopupPosition, IReactionTip } from "./comments-panel-types";

interface ICommentReactionsProps {
  comment: ICommentDto;
  currentUserId: string | undefined;
  onToggleReaction: (commentId: string, emoji: string) => void;
}

interface ICommentReactionsState {
  pickerOpen: boolean;
  pickerPos: IPopupPosition;
  tip: IReactionTip | null;
}

// Self-contained reaction bar (pills + "who reacted" tooltip + "+" emoji picker). Owns its own
// picker/tooltip state; the panel just supplies onToggleReaction (persisted optimistically).
export class CommentReactions extends React.PureComponent<ICommentReactionsProps, ICommentReactionsState> {
  private rootRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();

  constructor(props: ICommentReactionsProps) {
    super(props);
    this.state = { pickerOpen: false, pickerPos: { top: 0, left: 0 }, tip: null };
  }

  componentWillUnmount(): void {
    document.removeEventListener("mousedown", this.handleOutside, true);
  }

  // Close on outside mousedown; clicking another comment's "+" thus closes this one (single-open).
  private handleOutside = (e: MouseEvent): void => {
    const target: EventTarget | null = e.target;
    if (!(target instanceof Node)) return;
    const inRoot: boolean = this.rootRef.current?.contains(target) ?? false;
    const inPicker: boolean = target instanceof Element && Boolean(target.closest(".comments-panel__emoji-picker"));
    if (!inRoot && !inPicker) this.closePicker();
  };

  private closePicker(): void {
    document.removeEventListener("mousedown", this.handleOutside, true);
    if (this.state.pickerOpen) this.setState({ pickerOpen: false });
  }

  private togglePicker = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (this.state.pickerOpen) {
      this.closePicker();
      return;
    }
    const rect: DOMRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    document.addEventListener("mousedown", this.handleOutside, true);
    this.setState({ pickerOpen: true, pickerPos: { top: rect.top, left: rect.left } });
  };

  private toggle(emoji: string): void {
    this.closePicker();
    this.props.onToggleReaction(this.props.comment.id, emoji);
  }

  private showTip = (e: React.MouseEvent, r: ICommentReaction): void => {
    const { currentUserId } = this.props;
    const names: string[] = (r.reactedUserIds ?? [])
      .map((id: string) => (id === currentUserId ? "You" : displayNameOf(id)))
      .filter((n: string) => Boolean(n));
    if (!names.length) return;
    const rect: DOMRect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    this.setState({ tip: { names, emoji: r.emoji, top: rect.top, left: rect.left + rect.width / 2 } });
  };

  private hideTip = (): void => {
    if (this.state.tip) this.setState({ tip: null });
  };

  render(): React.ReactNode {
    const { comment } = this.props;
    const reactions: ICommentReaction[] = comment.reactions ?? [];
    const { pickerOpen, pickerPos, tip } = this.state;
    return (
      <div className="comments-panel__reactions" ref={this.rootRef}>
        {reactions.map((r: ICommentReaction) => (
          <button
            key={r.emoji}
            className={`comments-panel__reaction-pill${r.reactedByCurrentUser ? " comments-panel__reaction-pill--active" : ""}`}
            onMouseEnter={(e: React.MouseEvent): void => this.showTip(e, r)}
            onMouseLeave={this.hideTip}
            onClick={(e: React.MouseEvent): void => {
              e.stopPropagation();
              this.toggle(r.emoji);
            }}
            type="button"
          >
            {r.emoji} {r.count}
          </button>
        ))}
        <button className="comments-panel__reaction-add" onClick={this.togglePicker} type="button" title="Add reaction">
          +
        </button>

        {pickerOpen &&
          createPortal(
            <div
              className="comments-panel__emoji-picker"
              style={{ bottom: window.innerHeight - pickerPos.top + 6, left: pickerPos.left }}
              onClick={(e: React.MouseEvent): void => e.stopPropagation()}
            >
              {EMOJI_SET.map((emoji: string) => (
                <button
                  key={emoji}
                  className="comments-panel__emoji-btn"
                  onClick={(): void => this.toggle(emoji)}
                  type="button"
                >
                  {emoji}
                </button>
              ))}
            </div>,
            document.body
          )}

        {tip &&
          createPortal(
            <div
              className="comments-panel__reaction-tip"
              style={{ bottom: window.innerHeight - tip.top + 8, left: tip.left }}
            >
              <span className="comments-panel__reaction-tip-emoji">{tip.emoji}</span>
              <span className="comments-panel__reaction-tip-names">{formatReactors(tip.names)}</span>
            </div>,
            document.body
          )}
      </div>
    );
  }
}
